import type { Job, JobStatus, Page } from '@servicetim/shared';
import { useQuery } from '@tanstack/react-query';
import { adminPath, portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

/**
 * Jobs exist under both route groups: `/admin/v1/jobs` (configuration jobs: type widening,
 * status migration, ...) and `/v1/jobs` (bulk edits, exports). `surface` selects the group.
 */
export type JobSurface = 'admin' | 'portal';

const basePath = (surface: JobSurface) =>
  surface === 'admin' ? adminPath('/jobs') : portalPath('/jobs');

const TERMINAL: readonly JobStatus[] = ['done', 'failed'];

export function useJobs(
  options: { surface?: JobSurface; status?: JobStatus; pageSize?: number } = {},
) {
  const { request, keys } = useApiContext();
  const surface = options.surface ?? 'portal';
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  return usePagedList<Job>({
    queryKey: keys.jobs.list({ surface, status: options.status, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<Job>>('GET', basePath(surface), {
        query: { status: options.status, ...pageQuery(cursor, pageSize) },
        signal,
      }),
  });
}

/**
 * One job. While it is queued/running the query re-polls every `pollMs` (default 3 s) until it
 * reaches a terminal status; pass `pollMs: false` to disable.
 */
export function useJob(
  jobId: string | undefined,
  options: { surface?: JobSurface; pollMs?: number | false } = {},
) {
  const { request, keys } = useApiContext();
  const surface = options.surface ?? 'portal';
  const pollMs = options.pollMs ?? 3_000;
  return useQuery({
    queryKey: [...keys.jobs.detail(jobId ?? ''), surface],
    queryFn: ({ signal }) => request<Job>('GET', `${basePath(surface)}/${jobId}`, { signal }),
    enabled: jobId !== undefined,
    refetchInterval: (query) => {
      if (pollMs === false) return false;
      const status = query.state.data?.status;
      return status && TERMINAL.includes(status) ? false : pollMs;
    },
  });
}
